import { PHASE } from "../../../../common/constants.ts";
import type { Phase } from "../../../../common/types.ts";
import { helpers } from "../util/helpers.ts";
import i18n from "../util/i18n.ts";
import { getPhaseRoute } from "../util/phaseNavigation.ts";
import { translatePhaseText } from "../util/phaseText.ts";
import { MobileSheet } from "./MobileSheet.tsx";

// G6: the global phase banner (NavBar) is now a button that opens this lightweight
// "time bar" tool sheet instead of navigating straight to the phase route. The
// sheet answers "where am I in the season, and what's next?" with only data that
// is cheaply true GLOBALLY (from local state) — no new worker compute.
//
// Data honesty (法则: 推不出的节点不显示):
//   - Phase name + a one-line description of the current phase are always cheap.
//   - The engine ships no cheap "days until the next phase/deadline" figure, and
//     opening a worker computation just to count them is out of scope. So the only
//     forward "node" shown is the boolean the play menu already proves: the trade
//     deadline is still AHEAD exactly while the `untilTradeDeadline` advance option
//     is offered (updatePlayMenu drops it once the deadline passes). We show that
//     as a status row — no invented day count.
//   - The single CTA navigates to the current phase's home route (getPhaseRoute),
//     preserving the affordance the old banner link carried.
//
// Opening semantics follow MOBILE_SHEET_CONTRACT_V1 (component state only, no back
// stack) — that is inherited from MobileSheet; nothing here touches history.

const phaseDescription = (phase: Phase): string => {
	switch (phase) {
		case PHASE.PRESEASON:
			return i18n.t("phaseBar.desc.preseason", {
				ns: "ui",
				defaultValue:
					"Preseason: shape the roster and get ready for opening night.",
			});
		case PHASE.REGULAR_SEASON:
		case PHASE.AFTER_TRADE_DEADLINE:
			return i18n.t("phaseBar.desc.regularSeason", {
				ns: "ui",
				defaultValue:
					"The regular season is on — win games and work the market to climb the standings.",
			});
		case PHASE.PLAYOFFS:
			return i18n.t("phaseBar.desc.playoffs", {
				ns: "ui",
				defaultValue:
					"Playoffs: every round is win-or-go-home on the way to the title.",
			});
		case PHASE.DRAFT_LOTTERY:
			return i18n.t("phaseBar.desc.draftLottery", {
				ns: "ui",
				defaultValue:
					"Draft lottery: the offseason begins as your pick order is decided.",
			});
		case PHASE.DRAFT:
		case PHASE.FANTASY_DRAFT:
			return i18n.t("phaseBar.desc.draft", {
				ns: "ui",
				defaultValue: "Draft night: use your picks to add prospects.",
			});
		case PHASE.AFTER_DRAFT:
			return i18n.t("phaseBar.desc.afterDraft", {
				ns: "ui",
				defaultValue: "Post-draft: welcome the rookies before free agency.",
			});
		case PHASE.RESIGN_PLAYERS:
			return i18n.t("phaseBar.desc.resignPlayers", {
				ns: "ui",
				defaultValue:
					"Re-signing: lock up your own core before free agency opens.",
			});
		case PHASE.FREE_AGENCY:
			return i18n.t("phaseBar.desc.freeAgency", {
				ns: "ui",
				defaultValue: "Free agency: sign players to fill out the roster.",
			});
		case PHASE.EXPANSION_DRAFT:
			return i18n.t("phaseBar.desc.expansionDraft", {
				ns: "ui",
				defaultValue:
					"Expansion draft: protect your players before the new team picks.",
			});
		default:
			return i18n.t("phaseBar.desc.default", {
				ns: "ui",
				defaultValue:
					"Season progress: handle the main task for the current phase.",
			});
	}
};

export const PhaseBarSheet = ({
	open,
	onClose,
	phase,
	phaseText,
	playMenuOptionIds,
}: {
	open: boolean;
	onClose: () => void;
	phase: Phase;
	phaseText: string;
	// The play-menu option ids already in local state — used only for the cheap
	// "is the trade deadline still ahead?" boolean.
	playMenuOptionIds: string[];
}) => {
	const title =
		phaseText !== ""
			? translatePhaseText(phaseText)
			: i18n.t("phaseBar.sheetTitle", {
					ns: "ui",
					defaultValue: "Season progress",
				});

	// Cheap forward node: the deadline is still ahead exactly while its advance
	// option is offered. Only meaningful in the trading phases; drop it otherwise.
	const tradeDeadlineAhead =
		(phase === PHASE.REGULAR_SEASON || phase === PHASE.PRESEASON) &&
		playMenuOptionIds.includes("untilTradeDeadline");

	const phaseRouteHref = helpers.leagueUrl(getPhaseRoute(phase));

	return (
		<MobileSheet
			onClose={onClose}
			open={open}
			title={title}
			footer={
				<a
					className="btn btn-primary w-100"
					data-phase-bar-cta=""
					href={phaseRouteHref}
				>
					{i18n.t("phaseBar.goToPhase", {
						ns: "ui",
						defaultValue: "Go to the current phase",
					})}
				</a>
			}
		>
			<p className="mb-0" data-phase-bar-desc="">
				{phaseDescription(phase)}
			</p>
			{tradeDeadlineAhead ? (
				<div
					className="mt-3 d-flex align-items-center gap-2"
					data-phase-bar-node="tradeDeadline"
				>
					<span className="badge text-bg-secondary">
						{i18n.t("phaseBar.nodeLabel", {
							ns: "ui",
							defaultValue: "Ahead",
						})}
					</span>
					<span className="small text-body-secondary">
						{i18n.t("phaseBar.tradeWindowOpen", {
							ns: "ui",
							defaultValue:
								"The trade window is open — the deadline hasn't passed yet.",
						})}
					</span>
				</div>
			) : null}
		</MobileSheet>
	);
};
